/**
 * Urgent vs info for the technician's alerts (FM Technician rebuild B16, C4;
 * SPEC §0.1a "Class" column).
 *
 * THE SAME MAP AS backend/services/tech_alerts.py ALERT_CLASS — one map in two
 * languages. backend/tests/test_fm_technician_b16_alerts.py reads this file and
 * fails when an entry differs, so change both together.
 *
 * `p1` means urgent when the job is P1 (critical / p1 / emergency), else info:
 * a new job is urgent only when it is a P1 (§0.1a job_assigned_to_me).
 * Anything not listed is info.
 */
export type AlertClass = 'urgent' | 'info';

export const TECH_ALERT_CLASS: Readonly<Record<string, AlertClass | 'p1'>> = {
  'incident_reported': 'urgent',
  'incident_seen': 'info',
  'permit_expiring': 'urgent',
  'permit_expired': 'urgent',
  'emergency_need_help': 'urgent',
  'job_reassigned_away': 'urgent',
  'job_cancelled': 'urgent',
  'job_assigned_to_me': 'p1',
  'job_declined': 'urgent',
  'fault_reported_by_technician': 'urgent',
  'part_requested': 'info',
  'part_request_decided': 'info',
  'part_arrived': 'info',
  'signature_pending': 'info',
  'job_message': 'info',
  'asset_down': 'urgent',
  'ppm_cant_do': 'info',
  'job_conflict': 'info',
  'handover_received': 'info',
  'handover_unread': 'urgent',
  'job_sent_back': 'urgent',
  'job_moved_by_lead': 'info',
  'job_taken': 'info',
  'job_disputed': 'info',
  'dispute_resolved': 'info',
  'test_alert': 'info',
  'round_due': 'info',
  'permit_requested': 'urgent',
  'confined_exception_decided': 'urgent',
  'tool_calibration_due': 'info',
  'check_on_me_missed': 'urgent',
  'emergency_job_started': 'urgent',
  'sos_raised': 'urgent',
  'spot_check_failed': 'urgent',
  'emergency.alert': 'urgent',
  'sla_breach': 'urgent',
  'maintenance_alert': 'urgent',
  'compliance_overdue': 'urgent',
  'visitor_arrival': 'urgent',
  'account.impersonated': 'urgent',
};

const P1_PRIORITIES = new Set(['critical', 'p1', 'emergency']);

/** Whether a notification's data says its job is P1. */
export function isP1(data: Record<string, unknown> | null | undefined): boolean {
  const p = data && typeof data.priority === 'string' ? data.priority.toLowerCase() : '';
  return P1_PRIORITIES.has(p);
}

/** "urgent" or "info" for one notification. */
export function alertClass(event: string | null | undefined, data?: Record<string, unknown> | null): AlertClass {
  const cls = TECH_ALERT_CLASS[event ?? ''] ?? 'info';
  if (cls === 'p1') return isP1(data) ? 'urgent' : 'info';
  return cls;
}

/** The P1 sound plays for an urgent alert about a P1 job, and for the
 *  always-through safety alerts (they are P1 by nature). */
export function wantsP1Sound(event: string | null | undefined, data?: Record<string, unknown> | null): boolean {
  if (alertClass(event, data) !== 'urgent') return false;
  if (isP1(data)) return true;
  return ['incident_reported', 'sos_raised', 'check_on_me_missed', 'emergency_job_started',
    'emergency.alert', 'emergency_need_help'].includes(event ?? '');
}
